/**
 * Payment API Service
 * Client for communicating with the Payment Service backend
 * 
 * Requirements: 1.1, 2.1, 2.2, 2.4, 2.5, 2.6, 3.2
 */

/**
 * Payment Service base URL from environment variable
 * Defaults to localhost:3001 for development
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * Default timeout for API requests (30 seconds)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Creates an AbortController with timeout
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Object} - Object with controller and timeoutId
 */
function createTimeoutController(timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Handles API response and extracts JSON data
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} - Parsed JSON response
 * @throws {Error} - If response is not ok or parsing fails
 */
async function handleResponse(response) {
  const data = await response.json();
  
  if (!response.ok) {
    const errorMessage = data.error || data.errorMessage || 'Error en el servidor';
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

/**
 * Creates a new payment transaction
 * Sends enrollment token or enrollment data to the payment service and receives WebPay redirect info
 * 
 * The preferred flow is to use enrollmentToken from a verified enrollment.
 * Raw form data is supported for backward compatibility but will be deprecated.
 * 
 * @param {Object} paymentData - Payment request data
 * @param {string} [paymentData.enrollmentToken] - Token from verified enrollment (preferred)
 * @param {string} [paymentData.nombre] - Student full name (legacy flow)
 * @param {string} [paymentData.email] - Student email (legacy flow)
 * @param {string} [paymentData.rut] - Student RUT (formatted: XX.XXX.XXX-X) (legacy flow)
 * @param {string} [paymentData.telefono] - Student phone (optional)
 * @param {string} [paymentData.comoNosConociste] - Referral source (legacy flow)
 * @param {Array} [paymentData.cursos] - Array of course objects {id, name, price} (legacy flow)
 * @param {number} [paymentData.totalPrice] - Total amount in CLP (legacy flow)
 * @param {string} [paymentData.recaptchaToken] - reCAPTCHA verification token (legacy flow)
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Payment creation response
 * @returns {boolean} return.success - Whether creation was successful
 * @returns {string} return.orderId - Internal order ID
 * @returns {string} return.buyOrder - WebPay buy order reference
 * @returns {string} return.webpayUrl - URL to redirect user to WebPay
 * @returns {string} return.webpayToken - Token for WebPay form submission
 * @throws {Error} - If request fails or server returns error
 * 
 * Requirements: 6.1, 6.2, 6.3
 */
export async function createPayment(paymentData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/payments/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    // Re-throw if it's already a handled error
    if (error.status) {
      throw error;
    }

    // Network or other errors
    throw new Error('Error de conexión. Por favor verifica tu conexión a internet e intenta nuevamente.');
  }
}

/**
 * Creates a payment using an enrollment token from a verified enrollment
 * This is the preferred method for creating payments after email verification
 * 
 * @param {string} enrollmentToken - Token from verified enrollment
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Payment creation response
 * @returns {boolean} return.success - Whether creation was successful
 * @returns {string} return.orderId - Internal order ID
 * @returns {string} return.buyOrder - WebPay buy order reference
 * @returns {string} return.webpayUrl - URL to redirect user to WebPay
 * @returns {string} return.webpayToken - Token for WebPay form submission
 * @throws {Error} - If request fails, enrollment not verified, or server returns error
 * 
 * Requirements: 6.1, 6.2, 6.3
 */
export async function createPaymentWithToken(enrollmentToken, options = {}) {
  return createPayment({ enrollmentToken }, options);
}

/**
 * Confirms a payment after WebPay redirect
 * Called when user returns from WebPay payment form
 * 
 * @param {Object} confirmData - Confirmation data from WebPay redirect
 * @param {string} [confirmData.token_ws] - Success flow token
 * @param {string} [confirmData.TBK_TOKEN] - Abort flow token
 * @param {string} [confirmData.TBK_ORDEN_COMPRA] - Buy order for abort/timeout
 * @param {string} [confirmData.TBK_ID_SESION] - Session ID for timeout
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Payment confirmation response
 * @returns {boolean} return.success - Whether payment was successful
 * @returns {string} return.status - Payment status (approved, rejected, cancelled, timeout, error)
 * @returns {string} return.orderId - Internal order ID
 * @returns {string} return.buyOrder - WebPay buy order reference
 * @returns {Object} [return.transactionDetails] - Transaction details (on success)
 * @returns {string} [return.errorMessage] - Error message (on failure)
 * @throws {Error} - If request fails or server returns error
 */
export async function confirmPayment(confirmData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/payments/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(confirmData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La confirmación ha excedido el tiempo de espera. Por favor verifica el estado de tu pago.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al confirmar el pago. Por favor verifica el estado de tu pago.');
  }
}

/**
 * Gets the status of a payment by buy order
 * 
 * @param {string} buyOrder - WebPay buy order reference
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Payment status response
 * @returns {string} return.orderId - Internal order ID
 * @returns {string} return.buyOrder - WebPay buy order reference
 * @returns {string} return.status - Payment status
 * @returns {number} return.amount - Payment amount
 * @returns {string} return.createdAt - Order creation timestamp
 * @returns {string} return.updatedAt - Last update timestamp
 * @throws {Error} - If request fails or order not found
 */
export async function getPaymentStatus(buyOrder, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/payments/status/${encodeURIComponent(buyOrder)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La consulta ha excedido el tiempo de espera.');
    }

    if (error.status === 404) {
      throw new Error('Orden no encontrada.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al consultar el estado del pago.');
  }
}

/**
 * Redirects user to WebPay payment form
 * Creates a form and submits it via POST to WebPay URL
 * 
 * @param {string} webpayUrl - WebPay form URL
 * @param {string} webpayToken - WebPay token for form submission
 */
export function redirectToWebPay(webpayUrl, webpayToken) {
  // Create a hidden form for POST redirect
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = webpayUrl;
  form.style.display = 'none';

  // Add token_ws parameter
  const tokenInput = document.createElement('input');
  tokenInput.type = 'hidden';
  tokenInput.name = 'token_ws';
  tokenInput.value = webpayToken;
  form.appendChild(tokenInput);

  // Append to body and submit
  document.body.appendChild(form);
  form.submit();
}

/**
 * Parses URL query parameters from WebPay redirect
 * Extracts payment confirmation parameters from current URL
 * 
 * @param {string} [search] - URL search string (defaults to window.location.search)
 * @returns {Object} - Parsed parameters
 * @returns {string} [return.token_ws] - Success flow token
 * @returns {string} [return.TBK_TOKEN] - Abort flow token
 * @returns {string} [return.TBK_ORDEN_COMPRA] - Buy order for abort/timeout
 * @returns {string} [return.TBK_ID_SESION] - Session ID for timeout
 */
export function parseWebPayParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  
  return {
    token_ws: params.get('token_ws') || undefined,
    TBK_TOKEN: params.get('TBK_TOKEN') || undefined,
    TBK_ORDEN_COMPRA: params.get('TBK_ORDEN_COMPRA') || undefined,
    TBK_ID_SESION: params.get('TBK_ID_SESION') || undefined,
  };
}

/**
 * Determines the payment result type from WebPay parameters
 * 
 * @param {Object} params - WebPay redirect parameters
 * @returns {string} - Result type: 'success', 'cancelled', 'timeout', or 'unknown'
 */
export function getPaymentResultType(params) {
  if (params.token_ws) {
    return 'success';
  }
  if (params.TBK_TOKEN) {
    return 'cancelled';
  }
  if (params.TBK_ORDEN_COMPRA && params.TBK_ID_SESION) {
    return 'timeout';
  }
  return 'unknown';
}

/**
 * Checks if the payment service is in test/integration mode
 * 
 * @returns {boolean} - True if in test mode
 */
export function isTestMode() {
  const env = import.meta.env.VITE_WEBPAY_ENVIRONMENT || 'integration';
  return env !== 'production';
}

/**
 * Requests email verification for a new enrollment
 * Sends enrollment data to the verification service
 * 
 * @param {Object} verificationData - Verification request data
 * @param {string} verificationData.nombre - Student full name
 * @param {string} verificationData.email - Student email
 * @param {string} verificationData.rut - Student RUT (formatted: XX.XXX.XXX-X)
 * @param {string} [verificationData.telefono] - Student phone (optional)
 * @param {string} verificationData.comoNosConociste - Referral source
 * @param {Array} verificationData.cursos - Array of course objects {id, name, price}
 * @param {number} verificationData.totalPrice - Total amount in CLP
 * @param {string} verificationData.recaptchaToken - reCAPTCHA verification token
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Verification request response
 * @returns {boolean} return.success - Whether request was successful
 * @returns {boolean} return.verificationRequired - Whether verification is needed
 * @returns {string} return.enrollmentToken - Token to track this enrollment session
 * @returns {string} return.message - Status message
 * @returns {string} [return.expiresAt] - ISO timestamp when code expires
 * @returns {boolean} [return.isExistingUser] - True if user already has Moodle account
 * @returns {boolean} [return.isPreviouslyVerified] - True if email was previously verified
 * @throws {Error} - If request fails or server returns error
 * 
 * Requirements: 1.1, 2.2
 */
export async function requestVerification(verificationData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/verification/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(verificationData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet e intenta nuevamente.');
  }
}

/**
 * Verifies the code entered by user
 * 
 * @param {Object} verifyData - Verification data
 * @param {string} verifyData.enrollmentToken - Token from requestVerification
 * @param {string} verifyData.code - 6-digit verification code
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Verification response
 * @returns {boolean} return.success - Whether verification was successful
 * @returns {boolean} return.verified - Whether code was valid
 * @returns {number} [return.attemptsRemaining] - Remaining verification attempts
 * @returns {string} [return.error] - Error message if verification failed
 * @throws {Error} - If request fails or server returns error
 * 
 * Requirements: 2.2
 */
export async function verifyCode(verifyData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/verification/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(verifyData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La verificación ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al verificar el código.');
  }
}

/**
 * Resends verification code
 * 
 * @param {Object} resendData - Resend request data
 * @param {string} resendData.enrollmentToken - Token from requestVerification
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Resend response
 * @returns {boolean} return.success - Whether resend was successful
 * @returns {number} return.resendsRemaining - Remaining resend attempts
 * @returns {string} return.expiresAt - ISO timestamp when new code expires
 * @returns {string} [return.error] - Error message if resend failed
 * @throws {Error} - If request fails or server returns error
 * 
 * Requirements: 3.2
 */
export async function resendCode(resendData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/verification/resend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resendData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al reenviar el código.');
  }
}

/**
 * Gets the current verification status
 * 
 * @param {string} enrollmentToken - Token from requestVerification
 * @param {Object} [options] - Request options
 * @param {number} [options.timeout] - Request timeout in ms (default: 30000)
 * @returns {Promise<Object>} - Status response
 * @returns {boolean} return.success - Whether request was successful
 * @returns {string} return.status - Verification status (pending, verified, expired, failed)
 * @returns {string} return.email - User's email
 * @returns {number} return.attemptsRemaining - Remaining verification attempts
 * @returns {number} return.resendsRemaining - Remaining resend attempts
 * @returns {string} return.expiresAt - ISO timestamp when code expires
 * @throws {Error} - If request fails or enrollment not found
 */
export async function getVerificationStatus(enrollmentToken, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/verification/status/${encodeURIComponent(enrollmentToken)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La consulta ha excedido el tiempo de espera.');
    }

    if (error.status === 404) {
      throw new Error('Sesión no encontrada. Por favor, reinicia el proceso.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al consultar el estado de verificación.');
  }
}

export default {
  createPayment,
  createPaymentWithToken,
  confirmPayment,
  getPaymentStatus,
  redirectToWebPay,
  parseWebPayParams,
  getPaymentResultType,
  isTestMode,
  requestVerification,
  verifyCode,
  resendCode,
  getVerificationStatus,
};
